import type { ClientCustomer } from "api-types/customers/customersResponses";
import type { HomePlanCustomerInfo } from "api-types/modelTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import { ensureIsString } from "common/utils/assertOfType";
import { usePaginatedFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useEffect } from "react";
import type { InputGroupSelectProps } from "ui/forms/InputGroupSelect";
import InputGroupSelect from "ui/forms/InputGroupSelect";

export default function CustomerLookupInputGroup({
  labelText = "Customer Name",
  onChange,
  ...props
}: { onChange: (customerInfo: HomePlanCustomerInfo) => void } & Partial<Pick<InputGroupSelectProps, "labelText">> &
  Omit<InputGroupSelectProps, "labelText" | "options" | "onChange">) {
  const { builderId } = useAppRouterParams();
  const { data, setSearch } = usePaginatedFetch<ClientCustomer>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/customers`,
    [builderId],
    { pageSize: 50, debounceTimeout: 500 },
  );
  useEffect(() => {
    setSearch((props.value || "").toString());
  }, [props.value]);
  return (
    <InputGroupSelect
      labelText="Customer Name"
      placeholder="Enter Customer Name"
      options={
        data?.data?.map?.(({ name, phone, email, externalId }) => ({
          label: name ?? "",
          subLabel: email ? email : undefined,
          value: JSON.stringify({
            name: name ?? "",
            email: email ?? undefined,
            phone: phone ?? undefined,
            externalId: externalId ?? undefined,
          }),
          // enables searching by phone, externalId
          title: Object.values({ name, email, phone, externalId })
            .filter((v) => !!v)
            .join(", "),
        })) || []
      }
      allowInputWithoutSelection
      // when typing just the name
      inputOnChange={(name) => onChange({ name: ensureIsString(name) })}
      // when selecting an existing customer
      onChange={(value) => onChange(JSON.parse(ensureIsString(value)))}
      {...props}
    />
  );
}
